import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import { ReactComponent as CloseIcon } from '../../../public/assets/css/ontario-design-system/icons/ontario-icon-close.svg';
import './PopupComponent.css';

interface CustomPopupProps {
  onClickOutside?: (closable?: boolean) => void;
  handleCustomEvent?: (event: any) => void;
  children: React.ReactNode;
  isVisible: boolean;
  id?: string;
  title?: string;
  closable?: boolean;
  handleModalClose?: () => void;
  customClass?: string;
  customContentClass?: string;
}

const CustomPopup = ({
  onClickOutside,
  handleCustomEvent,
  children,
  isVisible,
  id,
  title,
  closable,
  handleModalClose,
  customClass,
  customContentClass,
}: CustomPopupProps) => {
  const [show, setShow] = useState(false);
  const [previousFocus, setPreviousFocus] = useState<Element | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  console.log('CustomPopup render', ReactDOMServer.renderToString(children));

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      console.log('handleClickOutside');
      if (ref.current) {
        if (closable && handleModalClose) {
          const close = ref.current?.querySelector('.modal-close-button');
          if (close && close.contains(event.target)) {
            event.stopPropagation();
            handleModalClose();
          } else if (event.target.nodeName === 'BUTTON' && !ref.current.contains(event.target)) {
            handleModalClose();
          }
        } else if (!ref.current?.contains(event.target) && handleCustomEvent) {
          event.stopPropagation();
          handleCustomEvent(event);
        } else if (!ref.current?.contains(event.target) && onClickOutside) {
          event.stopPropagation();
          onClickOutside(closable);
        }

        if (handleCustomEvent)
          handleCustomEvent(event);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setPreviousFocus(document.activeElement);
      setShow(true);
    } else {
      setShow(false);
      if (previousFocus && typeof (previousFocus as HTMLElement).focus === 'function') {
        (previousFocus as HTMLElement).focus();
      }
    }
  }, [isVisible]);

  useLayoutEffect(() => {
    if (isVisible && ref.current) {
      const focusable = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length > 0) {
        (focusable[0] as HTMLElement).focus();
      } else {
        ref.current.focus();
      }
    }
  }, [isVisible]);

  useEffect((): void | (() => void) => {
    if (!isVisible) return;

    const focusableElements = ref.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      } else if (e.key === 'Escape' && closable && handleModalClose) {
        handleModalClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, closable, handleModalClose]);

  return (
    <div
      className={`${customClass ?? ""} submenu-content`}
      style={{
        visibility: show ? "visible" : "hidden",
      }}
      ref={ref}
      role='dialog'
      id='popup-id'
      aria-modal='true'
      aria-labelledby='popupTitle'
      aria-describedby='popupBody'
      tabIndex={-1}
    >
      <div className={`${customContentClass || ""} modal-content`}>
        {title && (
          <div className='modal-header'>
            <h2
              style={{ padding: "0.75rem 0.5rem" }}
              className='h5 modal-title'
              id='popupTitle'
            >
              {title}
            </h2>
            {closable && (
              <button
                type='button'
                aria-label='Close dialog'
                className='message_button modal-close-button'
              >
                <CloseIcon focusable='false' aria-hidden='true' />
              </button>
            )}
          </div>
        )}
        <div className='modal-body' id='popupBody'>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CustomPopup;
